import i18n from '@/i18n';

/**
 * Local phone code dictionary.
 *
 * The primary source of codes is the backend (`/dicts/country-phone-code/list`),
 * but it only holds a dozen countries. This list is used as a supplement:
 * everything missing from the backend response is appended, so clients no longer
 * have to ask for another country to be added by hand.
 *
 * Row format: [ISO 3166-1 alpha-2, dial code, name (EN), masks].
 * Masks describe the national part of the number (without the dial code),
 * several variants are separated by `|`; the first mask is used for input, the
 * rest only for length validation (see the GET_PHONE_CODES mutation).
 */
const COUNTRIES = [
  ['AF', '93', 'Afghanistan', '##-###-####'],
  ['AL', '355', 'Albania', '##-###-####'],
  ['DZ', '213', 'Algeria', '###-##-##-##'],
  ['AD', '376', 'Andorra', '###-###'],
  ['AO', '244', 'Angola', '###-###-###'],
  ['AG', '1', 'Antigua and Barbuda', '###-###-####'],
  ['AR', '54', 'Argentina', '##-####-####'],
  ['AM', '374', 'Armenia', '##-##-##-##'],
  ['AW', '297', 'Aruba', '###-####'],
  ['AU', '61', 'Australia', '###-###-###'],
  ['AT', '43', 'Austria', '###-#######|###-######|###-########'],
  ['AZ', '994', 'Azerbaijan', '##-###-##-##'],
  ['BS', '1', 'Bahamas', '###-###-####'],
  ['BH', '973', 'Bahrain', '####-####'],
  ['BD', '880', 'Bangladesh', '####-######'],
  ['BB', '1', 'Barbados', '###-###-####'],
  ['BY', '375', 'Belarus', '##-###-##-##'],
  ['BE', '32', 'Belgium', '###-##-##-##'],
  ['BZ', '501', 'Belize', '###-####'],
  ['BJ', '229', 'Benin', '##-##-##-##|##-##-##-##-##'],
  ['BM', '1', 'Bermuda', '###-###-####'],
  ['BT', '975', 'Bhutan', '##-###-###'],
  ['BO', '591', 'Bolivia', '####-####'],
  ['BA', '387', 'Bosnia and Herzegovina', '##-###-###'],
  ['BW', '267', 'Botswana', '##-###-###'],
  ['BR', '55', 'Brazil', '##-#####-####|##-####-####'],
  ['VG', '1', 'British Virgin Islands', '###-###-####'],
  ['BN', '673', 'Brunei', '###-####'],
  ['BG', '359', 'Bulgaria', '###-###-###|##-###-###'],
  ['BF', '226', 'Burkina Faso', '##-##-##-##'],
  ['BI', '257', 'Burundi', '##-##-####'],
  ['KH', '855', 'Cambodia', '##-###-###|##-###-####'],
  ['CM', '237', 'Cameroon', '#-##-##-##-##'],
  ['CA', '1', 'Canada', '###-###-####'],
  ['CV', '238', 'Cape Verde', '###-####'],
  ['KY', '1', 'Cayman Islands', '###-###-####'],
  ['CF', '236', 'Central African Republic', '##-##-##-##'],
  ['TD', '235', 'Chad', '##-##-##-##'],
  ['CL', '56', 'Chile', '#-####-####'],
  ['CN', '86', 'China', '###-####-####'],
  ['CO', '57', 'Colombia', '###-###-####'],
  ['KM', '269', 'Comoros', '###-####'],
  ['CG', '242', 'Congo', '##-###-####'],
  ['CD', '243', 'Congo (DRC)', '###-###-###'],
  ['CR', '506', 'Costa Rica', '####-####'],
  ['CI', '225', 'Cote d Ivoire', '##-##-##-##-##'],
  ['HR', '385', 'Croatia', '##-###-####|##-###-###'],
  ['CU', '53', 'Cuba', '#-###-####'],
  ['CW', '599', 'Curacao', '###-####'],
  ['CY', '357', 'Cyprus', '####-####'],
  ['CZ', '420', 'Czechia', '###-###-###'],
  ['DK', '45', 'Denmark', '##-##-##-##'],
  ['DJ', '253', 'Djibouti', '##-##-##-##'],
  ['DM', '1', 'Dominica', '###-###-####'],
  ['DO', '1', 'Dominican Republic', '###-###-####'],
  ['EC', '593', 'Ecuador', '##-###-####|#-###-####'],
  ['EG', '20', 'Egypt', '##-####-####'],
  ['SV', '503', 'El Salvador', '####-####'],
  ['GQ', '240', 'Equatorial Guinea', '###-###-###'],
  ['ER', '291', 'Eritrea', '#-###-###'],
  ['EE', '372', 'Estonia', '####-####|###-####'],
  ['SZ', '268', 'Eswatini', '####-####'],
  ['ET', '251', 'Ethiopia', '##-###-####'],
  ['FJ', '679', 'Fiji', '###-####'],
  ['FI', '358', 'Finland', '##-###-####|##-###-#####'],
  ['FR', '33', 'France', '#-##-##-##-##'],
  ['PF', '689', 'French Polynesia', '##-##-##-##'],
  ['GA', '241', 'Gabon', '##-##-##-##|#-##-##-##'],
  ['GM', '220', 'Gambia', '###-####'],
  ['GE', '995', 'Georgia', '###-##-##-##'],
  ['DE', '49', 'Germany', '###-#######|###-######|###-########'],
  ['GH', '233', 'Ghana', '##-###-####'],
  ['GI', '350', 'Gibraltar', '####-####'],
  ['GR', '30', 'Greece', '###-###-####'],
  ['GL', '299', 'Greenland', '##-##-##'],
  ['GD', '1', 'Grenada', '###-###-####'],
  ['GT', '502', 'Guatemala', '####-####'],
  ['GN', '224', 'Guinea', '###-##-##-##'],
  ['GW', '245', 'Guinea-Bissau', '###-###-###'],
  ['GY', '592', 'Guyana', '###-####'],
  ['HT', '509', 'Haiti', '##-##-####'],
  ['HN', '504', 'Honduras', '####-####'],
  ['HK', '852', 'Hong Kong', '####-####'],
  ['HU', '36', 'Hungary', '##-###-####'],
  ['IS', '354', 'Iceland', '###-####'],
  ['IN', '91', 'India', '#####-#####'],
  ['ID', '62', 'Indonesia', '###-###-####|###-###-###|###-####-####|###-####-#####'],
  ['IR', '98', 'Iran', '###-###-####'],
  ['IQ', '964', 'Iraq', '###-###-####'],
  ['IE', '353', 'Ireland', '##-###-####|##-###-###'],
  ['IL', '972', 'Israel', '##-###-####'],
  ['IT', '39', 'Italy', '###-###-####|###-###-###|###-####-####'],
  ['JM', '1', 'Jamaica', '###-###-####'],
  ['JP', '81', 'Japan', '##-####-####|##-###-####'],
  ['JO', '962', 'Jordan', '#-####-####'],
  ['KZ', '7', 'Kazakhstan', '###-###-####'],
  ['KE', '254', 'Kenya', '###-######'],
  ['KW', '965', 'Kuwait', '####-####'],
  ['KG', '996', 'Kyrgyzstan', '###-###-###'],
  ['LA', '856', 'Laos', '##-##-###-###|##-###-###'],
  ['LV', '371', 'Latvia', '####-####'],
  ['LB', '961', 'Lebanon', '##-###-###|#-###-###'],
  ['LS', '266', 'Lesotho', '####-####'],
  ['LR', '231', 'Liberia', '##-###-###|###-###-###'],
  ['LY', '218', 'Libya', '##-###-####'],
  ['LI', '423', 'Liechtenstein', '###-####'],
  ['LT', '370', 'Lithuania', '###-#####'],
  ['LU', '352', 'Luxembourg', '###-###-###|###-###'],
  ['MO', '853', 'Macao', '####-####'],
  ['MG', '261', 'Madagascar', '##-##-###-##'],
  ['MW', '265', 'Malawi', '###-###-###'],
  ['MY', '60', 'Malaysia', '##-###-####|##-####-####'],
  ['MV', '960', 'Maldives', '###-####'],
  ['ML', '223', 'Mali', '##-##-##-##'],
  ['MT', '356', 'Malta', '####-####'],
  ['MR', '222', 'Mauritania', '##-##-##-##'],
  ['MU', '230', 'Mauritius', '####-####'],
  ['MX', '52', 'Mexico', '##-####-####'],
  ['MD', '373', 'Moldova', '##-###-###'],
  ['MC', '377', 'Monaco', '##-##-##-##|###-###-###'],
  ['MN', '976', 'Mongolia', '##-##-####'],
  ['ME', '382', 'Montenegro', '##-###-###'],
  ['MA', '212', 'Morocco', '###-###-###'],
  ['MZ', '258', 'Mozambique', '##-###-####'],
  ['MM', '95', 'Myanmar', '##-###-####|##-####-####'],
  ['NA', '264', 'Namibia', '##-###-####'],
  ['NP', '977', 'Nepal', '###-###-####'],
  ['NL', '31', 'Netherlands', '#-########'],
  ['NC', '687', 'New Caledonia', '##-####'],
  ['NZ', '64', 'New Zealand', '##-###-####|##-###-###'],
  ['NI', '505', 'Nicaragua', '####-####'],
  ['NE', '227', 'Niger', '##-##-##-##'],
  ['NG', '234', 'Nigeria', '###-###-####'],
  ['MK', '389', 'North Macedonia', '##-###-###'],
  ['NO', '47', 'Norway', '###-##-###'],
  ['OM', '968', 'Oman', '####-####'],
  ['PK', '92', 'Pakistan', '###-#######'],
  ['PS', '970', 'Palestine', '###-###-###'],
  ['PA', '507', 'Panama', '####-####|###-####'],
  ['PG', '675', 'Papua New Guinea', '####-####'],
  ['PY', '595', 'Paraguay', '###-###-###'],
  ['PE', '51', 'Peru', '###-###-###'],
  ['PH', '63', 'Philippines', '###-###-####'],
  ['PL', '48', 'Poland', '###-###-###'],
  ['PT', '351', 'Portugal', '###-###-###'],
  ['PR', '1', 'Puerto Rico', '###-###-####'],
  ['QA', '974', 'Qatar', '####-####'],
  ['RO', '40', 'Romania', '###-###-###'],
  ['RU', '7', 'Russia', '###-###-##-##'],
  ['RW', '250', 'Rwanda', '###-###-###'],
  ['KN', '1', 'Saint Kitts and Nevis', '###-###-####'],
  ['LC', '1', 'Saint Lucia', '###-###-####'],
  ['VC', '1', 'Saint Vincent and the Grenadines', '###-###-####'],
  ['WS', '685', 'Samoa', '###-####'],
  ['SM', '378', 'San Marino', '###-###-####|##-###-###'],
  ['SA', '966', 'Saudi Arabia', '##-###-####'],
  ['SN', '221', 'Senegal', '##-###-##-##'],
  ['RS', '381', 'Serbia', '##-###-####|##-###-###'],
  ['SC', '248', 'Seychelles', '#-###-###'],
  ['SL', '232', 'Sierra Leone', '##-######'],
  ['SG', '65', 'Singapore', '####-####'],
  ['SK', '421', 'Slovakia', '###-###-###'],
  ['SI', '386', 'Slovenia', '##-###-###'],
  ['SB', '677', 'Solomon Islands', '###-####'],
  ['SO', '252', 'Somalia', '##-###-###|#-###-###'],
  ['ZA', '27', 'South Africa', '##-###-####'],
  ['KR', '82', 'South Korea', '##-####-####|#-####-####'],
  ['SS', '211', 'South Sudan', '##-###-####'],
  ['ES', '34', 'Spain', '###-##-##-##'],
  ['LK', '94', 'Sri Lanka', '##-###-####'],
  ['SD', '249', 'Sudan', '##-###-####'],
  ['SR', '597', 'Suriname', '###-####'],
  ['SE', '46', 'Sweden', '##-###-####|###-###-####'],
  ['CH', '41', 'Switzerland', '##-###-####'],
  ['SY', '963', 'Syria', '###-###-###'],
  ['TW', '886', 'Taiwan', '###-###-###'],
  ['TJ', '992', 'Tajikistan', '##-###-####'],
  ['TZ', '255', 'Tanzania', '###-###-###'],
  ['TH', '66', 'Thailand', '##-###-####|#-###-####'],
  ['TL', '670', 'Timor-Leste', '####-####'],
  ['TG', '228', 'Togo', '##-##-##-##'],
  ['TO', '676', 'Tonga', '###-####'],
  ['TT', '1', 'Trinidad and Tobago', '###-###-####'],
  ['TN', '216', 'Tunisia', '##-###-###'],
  ['TR', '90', 'Turkiye', '###-###-####'],
  ['TM', '993', 'Turkmenistan', '##-##-##-##'],
  ['UG', '256', 'Uganda', '###-######'],
  ['UA', '380', 'Ukraine', '##-###-##-##'],
  ['AE', '971', 'United Arab Emirates', '##-###-####'],
  ['GB', '44', 'United Kingdom', '####-######|###-###-###'],
  ['US', '1', 'United States', '###-###-####'],
  ['UY', '598', 'Uruguay', '##-###-###|###-###-###'],
  ['UZ', '998', 'Uzbekistan', '##-###-##-##'],
  ['VU', '678', 'Vanuatu', '###-####'],
  ['VE', '58', 'Venezuela', '###-###-####'],
  ['VN', '84', 'Vietnam', '###-###-###'],
  ['YE', '967', 'Yemen', '###-###-###'],
  ['ZM', '260', 'Zambia', '##-###-####'],
  ['ZW', '263', 'Zimbabwe', '##-###-####'],
];

const FLAG_CDN = 'https://flagcdn.com';

const displayNamesCache = {};

/**
 * Cached Intl.DisplayNames for the given locale.
 * Returns null when the browser does not support the API.
 */
const getDisplayNames = (locale) => {
  if (!(locale in displayNamesCache)) {
    try {
      displayNamesCache[locale] = new Intl.DisplayNames([locale], { type: 'region' });
    } catch (e) {
      displayNamesCache[locale] = null;
    }
  }
  return displayNamesCache[locale];
};

/**
 * Country name to display.
 * Priority: translation from locales -> Intl.DisplayNames -> dictionary name.
 * @param {Object} item - phone code list entry
 * @returns {String}
 */
export const countryDisplayName = (item) => {
  const region = item.isoCode2 || item.phoneRegion;

  if (region && i18n.te(`countries.titles.${region}`)) {
    return i18n.t(`countries.titles.${region}`);
  }

  const displayNames = getDisplayNames(i18n.locale);
  if (region && displayNames) {
    try {
      const name = displayNames.of(region);
      if (name && name !== region) return name;
    } catch (e) {
      // invalid region - fall back to the dictionary name
    }
  }

  return item.name;
};

/**
 * Local dictionary in the same shape the backend returns.
 * @returns {Array<Object>}
 */
export const buildLocalPhoneCodes = () => COUNTRIES.map(([isoCode2, dialCode, name, masks]) => {
  const phoneMasks = masks.split('|');
  const flagCode = isoCode2.toLowerCase();

  return {
    id: `local-${isoCode2}`,
    name,
    phoneCode: `+${dialCode}`,
    phoneRegion: isoCode2,
    isoCode2,
    phoneMasks,
    phoneMobileMask: phoneMasks[0],
    flagIcon: `${FLAG_CDN}/w40/${flagCode}.png`,
    flagIconWeb: `${FLAG_CDN}/${flagCode}.svg`,
  };
});

/**
 * Supplements the backend list with the local dictionary.
 * Backend countries keep their original order at the top of the list - both the
 * default selection and the code detection on paste depend on it.
 * @param {Array<Object>} apiCodes
 * @returns {Array<Object>}
 */
export const mergePhoneCodes = (apiCodes = []) => {
  const existing = new Set(apiCodes.map((item) => item.isoCode2 || item.phoneRegion).filter(Boolean));

  const extraCodes = buildLocalPhoneCodes()
    .filter((item) => !existing.has(item.isoCode2))
    .sort((a, b) => countryDisplayName(a).localeCompare(countryDisplayName(b), i18n.locale));

  return [...apiCodes, ...extraCodes];
};

/**
 * Search filter for the dropdown: by name, dial code and ISO country code.
 * @param {Array<Object>} codes
 * @param {String} query
 * @returns {Array<Object>}
 */
export const filterPhoneCodes = (codes, query) => {
  const search = (query || '').trim().toLowerCase();
  if (!search) return codes;

  const digits = search.replace(/[^\d]/g, '');

  return codes.filter((item) => {
    // Match both the localized and the English dictionary name - people type
    // latin names even with a non-english interface.
    const names = [item.displayName, item.name].filter(Boolean).map((name) => name.toLowerCase());
    if (names.some((name) => name.includes(search))) return true;
    if ((item.isoCode2 || item.phoneRegion || '').toLowerCase() === search) return true;
    return !!digits && item.phoneCode.replace('+', '').startsWith(digits);
  });
};

export default COUNTRIES;
